import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Divider, DrawerBody,
  DrawerCloseButton,
  Flex, Image,
  Text, VStack
} from '@chakra-ui/react';

import InsuranceDocumentBlock from '../../../../../../components/RequirementBlocks/InsuranceDocumentBlock';
import InsuranceLanguageBlock from '../../../../../../components/RequirementBlocks/InsuranceLanguageBlock';
import QuestionBlock from '../../../../../../components/RequirementBlocks/QuestionBlock';
import LoadingSpinner from '../../../../../../components/Spinner';
import nothingHereImage from '../../../../../../nothing-here.png';
import { globalRequirementTemplateBuilder } from '../../../../../../redux/reducer';
import { get } from '../../../../../../utils/arrayUtil';

import InsurancePolicyTemplateBlock from './TemplateBlocks/InsurancePolicyTemplateBlock';
import TemplatePreviewHeader from './TemplatePreviewHeader';

import './styles.css';

const EmptyTemplateMessage = () => (
  <Flex alignItems="center" direction="column" height="60%" justifyContent="center" width="100%">
    <Image src={nothingHereImage} width="70%" />
    <VStack mt={4}>
      <Text textStyle="bodyLg500">Nothing Here</Text>
      <Text textStyle="bodyMd500">Template has no requirements yet.</Text>
    </VStack>
  </Flex>
);

const TemplatePreviewTray = () => {
  const template = useSelector(globalRequirementTemplateBuilder.selectCurrentRequirementTemplate);

  const templateQuestions = useMemo(() => {
    if (!template) return [];

    const questionsFromBlocks = template.requirementTemplateBlocks.reduce((acc, block) => {
      if (block.requirementTemplateQuestions) {
        acc.push(...block.requirementTemplateQuestions);
      }

      return acc;
    }, []);

    return [...new Map(questionsFromBlocks.map(q => [q.id, q])).values()];
  }, [template]);

  if (!template) {
    return (
      <Box height="100%">
        <LoadingSpinner isInline minHeight="100%" />
      </Box>
    );
  }

  const isArchived = template.archivedAt;

  return (
    <Box height="100vh" id="print-template-preview" overflow="auto">
      <DrawerCloseButton />
      <TemplatePreviewHeader template={template} />

      <Divider borderBottomWidth="3px" />

      <DrawerBody mb={3} pt={3}>
        {template?.requirementTemplateBlocks?.length
          ? (
            <>
              {template.requirementTemplateBlocks.map(block => {
                if (!block.requirementTemplateBlockProperties[0]) return null;
                const { requirementType } = block.requirementTemplateBlockProperties[0];

                if (requirementType === 'insurance_policy_type_property') {
                  return <InsurancePolicyTemplateBlock isArchived={isArchived} requirementBlock={block} />;
                }

                if (requirementType === 'insurance_document_type') {
                  return (
                    <InsuranceDocumentBlock
                      description={get(block, 'requirementTemplateBlockProperties.0.requirement.description', '')}
                      displayName={get(block, 'requirementTemplateBlockProperties.0.requirement.displayName', '')}
                      isArchived={isArchived}
                      isRequired={block.required}
                    />
                  );
                }

                return (
                  <InsuranceLanguageBlock
                    description={get(block, 'requirementTemplateBlockProperties.0.requirement.language', '')}
                    displayName={get(block, 'requirementTemplateBlockProperties.0.requirement.displayName', '')}
                    isArchived={isArchived}
                    isRequired={block.required}
                  />
                );
              })}
              {!!templateQuestions?.length && (
                <>
                  {templateQuestions.map(question => (
                    <QuestionBlock key={question.id} question={question.question} />
                  ))}

                </>
              )}
            </>
          )
          : <EmptyTemplateMessage />}
      </DrawerBody>
    </Box>
  );
};

export default TemplatePreviewTray;
